// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleThirdPartyLicenses } from 'store/reducers/uiSettingsSlice';
import ThirdPartyLicenses from './ThirdPartyLicenses';
import { LicenseProvider, useBackBehavior } from '@tls/treact-ui-templates';
import styled from 'styled-components';
import { Dialog, FooterButton } from '@tls/treact-ui';

export function ThirdPartyLicensesDialog() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const onClose = useCallback(() => {
    dispatch(toggleThirdPartyLicenses());
  }, [dispatch]);
  const open = useAppSelector(s => s.uiSettings.showThirdPartyLicenses);

  return (
    <Dialog open={open} onClose={onClose} size={'huge'} enableClosing data-testid='third-party-licenses-dialog'>
      <LicenseProvider>
        <Dialog.DialogHeader label={t('thirdPartyLicenses.title')} />
        <Dialog.DialogContent>
          <ContentWrapper>
            <ThirdPartyLicenses />
          </ContentWrapper>
        </Dialog.DialogContent>
        <Footer onClose={onClose} />
      </LicenseProvider>
    </Dialog>
  );
}

const ContentWrapper = styled.div`
  height: calc(100% + 2rem);
  margin: -1rem;
`;

const Footer = ({ onClose }) => {
  const { t } = useTranslation();

  const { showBackButton, onBack } = useBackBehavior();

  const rightButtons = useMemo(() => {
    const buttons = [
      <FooterButton
        variant='secondary'
        key='cancel'
        label={t('close')}
        onClick={onClose}
        data-testid='third-party-licenses-close-button'
      />,
    ];
    if (showBackButton) {
      buttons.unshift(
        <FooterButton
          variant='primary'
          key='back'
          label={t('back')}
          onClick={onBack}
          data-testid='third-party-licenses-back-button'
        />
      );
    }
    return buttons;
  }, [onBack, onClose, showBackButton, t]);

  return <Dialog.Footer right={rightButtons} />;
};
